import { defineStore } from 'pinia'
import { fetchMe, type CapabilityName, type MeResponse } from '@/api/me'
import { authMe, logout as apiLogout, type AuthSession } from '@/api/auth'
import { ApiError } from '@/api/client'
import { useToast } from '@/composables/useToast'

// desktop-login P1：身份从"共享档口串(X-Tenant-Ulid)"切到"用户登录令牌"。
// 令牌持久化到 localStorage；请求由 client.ts 带 Authorization: Bearer。
// 兼容旧消费方：保留 `tenantUlid` getter（= 当前登录档口 ulid）、`me`/
// `hasCapability`/`activeSearchProvider`（仍来自 GET /me 的 capabilities）。

const TOKEN_KEY = 'fabric.user_token'

export const useAuthStore = defineStore('auth', {
  state: () => ({
    token: localStorage.getItem(TOKEN_KEY),
    /** 当前会话身份（user + tenant + must_change），来自 login / GET /auth/me */
    session: null as AuthSession | null,
    /** 能力/套餐（GET /me），用于功能显隐 */
    me: null as MeResponse | null,
    loading: false,
    error: null as string | null,
    /** bootstrap 只跑一次的标记 */
    bootstrapped: false,
    /** bootstrap 跑完(用令牌恢复身份完成)——之前不渲染欢迎门/内容,避免闪烁 */
    ready: false,
    /** 平台运营代登录(只读)模式 —— 经 ?impersonate= 进入,UI 显只读 banner。 */
    impersonating: false,
    /** 租户被停服(suspended/closed)时的提示文案;非 null = 全屏停服门,不清令牌、不进主界面。
     *  区别于普通令牌失效(那会清令牌回登录),停服要让用户看清原因而非陷入"登录→清空"循环。 */
    suspended: null as string | null,
    /** 令牌已过期并已触发过一次全局重登引导(P1-6)。用于并发 401 去重 + WelcomeGate 文案。
     *  下次拿到新令牌(setToken 有值)即复位。 */
    sessionExpired: false
  }),
  getters: {
    // me 加载成功 = 已认证（用户登录 或 免费档设备令牌都算）。
    isAuthenticated: (s): boolean => !!s.token && !!s.me,
    /** 免费档（设备令牌,无 user 身份）。 */
    isFreeDevice: (s): boolean => !!s.token && !!s.me && !s.session && !s.impersonating,
    /** 平台运营代登录(只读)。UI 据此显 banner;写操作后端已双保险拒绝。 */
    isImpersonating: (s): boolean => s.impersonating,
    mustChangePassword: (s): boolean => s.session?.must_change_password ?? false,
    role: (s): string | null => s.session?.user.role ?? null,
    isOwner: (s): boolean => s.session?.user.role === 'owner',
    currentUser: (s) => s.session?.user ?? null,
    /** 兼容旧用法：当前档口 ulid（用户会话优先,否则 me.tenant,未认证 = ''）。 */
    tenantUlid: (s): string => s.session?.tenant.ulid ?? s.me?.tenant.ulid ?? '',
    hasCapability:
      (s) =>
      (cap: CapabilityName): boolean =>
        s.me?.capabilities.some((c) => c.name === cap && c.enabled) ?? false,
    activeSearchProvider: (s): string | null =>
      s.me?.capabilities.find((c) => c.name === 'image_search')?.active_provider ?? null
  },
  actions: {
    setToken(t: string | null) {
      this.token = t
      if (t) {
        localStorage.setItem(TOKEN_KEY, t)
        this.sessionExpired = false // 新令牌落地 → 复位过期态,允许下次过期再引导
      } else {
        localStorage.removeItem(TOKEN_KEY)
      }
    },
    /** 令牌过期的统一处理(P1-6):client.ts 收到非登录接口的 401 时调。
     *  清登录态 → AppLayout 自动回 WelcomeGate;并发 401 只走一次(已过期态 / 无令牌即跳过)。 */
    onUnauthorized() {
      if (this.sessionExpired || !this.token) return
      this.sessionExpired = true
      this.setToken(null) // 注意:setToken(null) 不复位 sessionExpired,只有拿到新令牌才复位
      this.session = null
      this.me = null
      this.impersonating = false
      // suspended(403 停服)不在此处理,它有独立全屏门、且不清令牌
      useToast().error('登录已过期，请重新登录')
    },
    /** 登录成功后落地令牌 + 会话。 */
    afterLogin(token: string, session: AuthSession) {
      this.setToken(token)
      this.session = session
    },
    /** 免费档自助绑定成功:落地设备令牌(无 user 会话)。caller 随后 refreshMe()。 */
    afterFreeBind(token: string) {
      this.setToken(token)
      this.session = null
    },
    clearMustChange() {
      if (this.session) this.session.must_change_password = false
    },
    /** 拉能力/套餐（功能显隐）。 */
    async refreshMe() {
      if (!this.token) return
      try {
        this.me = await fetchMe()
        this.suspended = null
      } catch (e: unknown) {
        // 停服(账号暂停/注销)不同于令牌失效:后端返 403 tenant_suspended/tenant_closed。
        // 记下文案供全屏停服门展示,令牌保留(不走"清空回登录"路径,否则重登又被拒陷入死循环)。
        if (e instanceof ApiError && (e.code === 'tenant_suspended' || e.code === 'tenant_closed')) {
          this.suspended = e.message
        }
        this.error = e instanceof Error ? e.message : String(e)
        this.me = null
      }
    },
    /** 用持久化令牌校验 + 拉身份（GET /auth/me）。失败抛错由 bootstrap 兜底清理。 */
    async loadSession() {
      this.session = await authMe()
    },
    /** 启动只跑一次：有令牌就校验 + 拉能力；令牌失效则清空。
     *  用户登录令牌 → /auth/me 拿身份；免费档设备令牌 → /auth/me 会 401,
     *  但 /me（能力）仍通 → 算"免费档已认证"(session 留 null)。 */
    async bootstrap() {
      if (this.bootstrapped) return
      this.bootstrapped = true
      // 受控代登录:admin 用 #impersonate=<token> 打开桌面 web。token 放 URL
      // fragment(#)而非 query(?)—— fragment 不进 HTTP 请求、不进 Referer、
      // 不落服务端访问日志,泄漏面更小。当 user token 用,标记 impersonating
      // (UI 只读 banner),读完即抹掉 token(fragment + query 都清),不在
      // 地址栏 / 历史留痕。query 分支保留作过渡兼容(老 admin 仍发 ?impersonate=)。
      try {
        const url = new URL(window.location.href)
        const hashParams = new URLSearchParams(
          url.hash.startsWith('#') ? url.hash.slice(1) : url.hash
        )
        const impToken = hashParams.get('impersonate') ?? url.searchParams.get('impersonate')
        if (impToken) {
          this.setToken(impToken)
          this.impersonating = true
          url.searchParams.delete('impersonate')
          url.hash = ''
          window.history.replaceState({}, '', url.toString())
        }
      } catch {
        /* URL 解析失败忽略 */
      }
      try {
        if (!this.token) return
        await this.refreshMe() // /me 对两种令牌都通
        if (!this.me) {
          // 停服(账号暂停/注销):保留令牌,停在全屏停服门(refreshMe 已置 suspended)。
          if (this.suspended) return
          // 令牌失效 → 清空
          this.setToken(null)
          this.session = null
          return
        }
        // me 通过 → 已认证。再试拿用户身份(设备令牌会 401 → 免费档,session 留 null)
        try {
          await this.loadSession()
        } catch {
          this.session = null
        }
      } finally {
        this.ready = true
      }
    },
    async logout() {
      try {
        await apiLogout()
      } catch {
        /* 撤销失败也照样本地清空 */
      }
      this.setToken(null)
      this.session = null
      this.me = null
      this.impersonating = false
      this.suspended = null
    }
  }
})
